import React from 'react';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import Badge from '@components/common/badge/badge';
import ProductImage from '@components/atoms/vc-images/components/product-image';
import { GuidedSearchSuggestion } from '@interfaces/api/responses/elasticSearch';
import { PLP_SOURCE_CATEGORY_QUERY_KEY, PlpSourceCategory } from '@helpers/utils/catalog/catalog-tracking-utils';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Link from '@components/atoms/link/link';
import useWindowSize from '@hooks/use-window-size';
import styles from './catalog-guided-search.module.scss';

const maximumNumberOfItemsOnWebView = 8;

const isSuggestionValid = (suggestion: GuidedSearchSuggestion) =>
  !!suggestion.updatePayload?.q && !!suggestion.image && !!suggestion.label;

const IMAGE_URL_REGEX = /\/produit\/(\d+)-/;

const getSuggestionImageId = (suggestion: GuidedSearchSuggestion) => {
  // image urls are sent in the format /produit/63271653-1_1.jpg
  // here we parse the id from the path
  if (!suggestion.image) {
    return '';
  }
  const match = suggestion.image.match(IMAGE_URL_REGEX);
  return match ? String(match[1]) : '';
};

export enum GuidedSearchType {
  Taxonomy,
  SearchAPI,
}

interface CatalogGuidedSearchProps {
  guidedSearchType?: GuidedSearchType;
}

const CatalogGuidedSearch: React.FC<CatalogGuidedSearchProps> = ({ guidedSearchType = GuidedSearchType.Taxonomy }) => {
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const enableTaxonomyGuidedSearch = useContextSelector(CatalogContext, (v) => v.enableTaxonomyGuidedSearch);

  const suggestions = queryResult?.guidedSearch?.suggestions ?? [];
  const { sendAnalyticEvent } = useAnalyticEvents('catalog');

  const isDesktopView = useWindowSize().isWiderThanMd;

  // Don't render anything until we get the API response
  // Prevent from showing static guided search - server rendered - when taxonomy guided search is enabled
  if (!queryResult) {
    return null;
  }

  // Don't render if no suggestions available
  if (suggestions.length === 0) {
    return null;
  }

  // If GuidedSearchType.Taxonomy, check enableTaxonomyGuidedSearch condition
  if (guidedSearchType === GuidedSearchType.Taxonomy && enableTaxonomyGuidedSearch !== true) {
    return null;
  }

  return (
    <ul
      className={styles.catalogGuidedSearch__container}
      data-cy="catalog_guided_search_container"
    >
      {suggestions
        .filter((suggestion) => isSuggestionValid(suggestion))
        .slice(0, isDesktopView ? maximumNumberOfItemsOnWebView : undefined)
        .map((suggestion, index) => (
          <li
            key={`guided-search-badge-${index}`}
            className={styles.catalogGuidedSearch__item}
          >
            <Link
              href={`/search?q=${encodeURIComponent(suggestion.updatePayload.q)}&${PLP_SOURCE_CATEGORY_QUERY_KEY}=${
                PlpSourceCategory.guidedSearch
              }`}
              onClick={() => {
                sendAnalyticEvent('guided_search_chip_click', {
                  label: suggestion.label,
                  property: String(index + 1),
                  value: getSuggestionImageId(suggestion),
                });
              }}
              data-cy={`guided_search_badge_${index}`}
            >
              <Badge
                useFilterRedesignUi={true}
                isInteractive={false}
              >
                <ProductImage
                  wrapperProps={{ className: styles.catalogGuidedSearch__imageContainer }}
                  src={suggestion.image}
                  height={48}
                  width={48}
                  alt={suggestion.label}
                />
                {suggestion.label}
              </Badge>
            </Link>
          </li>
        ))}
    </ul>
  );
};

export default CatalogGuidedSearch;
